/**
 * Rotating text after the stock status (blocks/low-stock.liquid).
 *
 * Time on the store's clock is cut into slots of one interval. The texts are shuffled once per round of
 * [number of texts] slots and shown in that order, and a round that would start with the text the previous
 * round ended on swaps its first two texts. So the order is random, no text is shown twice in a row, every
 * text gets a turn before the order is reshuffled, every visitor sees the same text, and a refresh does not
 * restart the rotation. The block makes the same pick on the server with the same numbers (change both
 * together); this script corrects a cached page and moves an open page on to the next text at each interval
 * boundary, with a short fade.
 *
 * An element with a data-seed shuffles in its own order (the seed is added to every sort key), so its texts do
 * not change in step with another block's. Without one the seed is 0: the shared order.
 *
 * The element sits inside <product-inventory>, whose content assets/product-inventory.js morphs back to a
 * fresh server copy when the customer picks another variant, so any change inside it re-applies the pick.
 */

/** A prime below 2^26, so every product below stays a whole number in a double, as it does in Liquid */
const MODULUS = 67108859;

/**
 * The order of the texts in one round: sorted by a pseudo-random number per text, ties by text number.
 * @param {number} round
 * @param {number} count - The number of texts
 * @param {number} [seed] - The block's own seed; 0 is the shared order
 * @returns {number[]}
 */
function roundOrder(round, count, seed = 0) {
  /** @type {[number, number][]} */
  const keys = [];

  for (let text = 0; text < count; text++) {
    let x = (round * 131 + text * 7919 + 17 + seed) % MODULUS;
    x = (x * x + 11) % MODULUS;
    x = (x * x + 23) % MODULUS;
    x = (x * x + 37) % MODULUS;
    keys.push([x, text]);
  }

  return keys.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map(([, text]) => text);
}

/**
 * Which text is shown in a slot.
 * @param {number} slot - Whole intervals since 1970 on the store's clock
 * @param {number} count - The number of texts
 * @param {number} [seed] - The block's own seed; 0 is the shared order
 */
function textForSlot(slot, count, seed = 0) {
  if (count < 2) return 0;
  if (count === 2) return (slot + seed) % 2;

  const round = Math.floor(slot / count);
  const position = slot % count;
  const order = roundOrder(round, count, seed);

  if (position < 2 && order[0] === roundOrder(round - 1, count, seed)[count - 1]) {
    [order[0], order[1]] = [order[1], order[0]];
  }

  return order[position];
}

class StockRotator extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  #observer = new MutationObserver(() => this.#update());

  connectedCallback() {
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-interval', 'data-offset', 'data-seed'],
    });
    document.addEventListener('visibilitychange', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener('visibilitychange', this.#update);
    clearTimeout(this.#timer);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return Array.from(this.querySelectorAll(':scope > [data-rotator-item]'));
  }

  /**
   * Which text the store's clock is on, and the milliseconds until it moves on.
   * @param {number} count - The number of texts
   */
  #clock(count) {
    const interval = Number(this.dataset.interval);
    const now = Date.now() / 1000 + (Number(this.dataset.offset) || 0);
    const slot = Math.floor(now / interval);
    const seed = Number(this.dataset.seed) || 0;

    return { index: textForSlot(slot, count, seed), wait: ((slot + 1) * interval - now) * 1000 };
  }

  /**
   * Shows the current text and waits for the next interval boundary.
   */
  #update = () => {
    clearTimeout(this.#timer);

    const items = this.#items;
    if (!this.isConnected || items.length === 0 || !(Number(this.dataset.interval) > 0)) return;

    const { index, wait } = this.#clock(items.length);

    items.forEach((item, i) => {
      // Only write real changes: this also runs from the mutation observer
      if (item.hidden !== (i !== index)) item.hidden = i !== index;
    });

    // A moment past the boundary, so the clock is safely on the next text
    if (items.length > 1) this.#timer = setTimeout(this.#advance, wait + 500);
  };

  /**
   * At an interval boundary: fades the current text out, then the next one in.
   */
  #advance = () => {
    const items = this.#items;
    const current = items.find((item) => !item.hidden);
    const next = items[this.#clock(items.length).index];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!current || !next || next === current || document.hidden || reduceMotion) {
      this.#update();
      return;
    }

    const fadeOut = current.animate({ opacity: [1, 0] }, { duration: 200, easing: 'ease-in', fill: 'forwards' });
    // Switch even if the fade never finishes (the browser can pause animations while nothing is painted)
    const fadeTimeout = new Promise((resolve) => setTimeout(resolve, 400));

    Promise.race([fadeOut.finished, fadeTimeout])
      .catch(() => {})
      .then(() => {
        this.#update();
        fadeOut.cancel();
        // Normally the next text; the faded-out one again if the pick changed back during the fade
        this.#items.find((item) => !item.hidden)?.animate({ opacity: [0, 1] }, { duration: 200, easing: 'ease-out' });
      });
  };
}

if (!customElements.get('gm-stock-rotator')) {
  customElements.define('gm-stock-rotator', StockRotator);
}
